import { useQuery } from "@tanstack/react-query";
import useGymflowModels from "apps/portal/src/store";
import { apiV2 } from "libs/api/src/lib/apiV2";
import { useStaffGetInvoiceReceipt } from "libs/api/src/lib/hooks/staff/useStaffGetInvoiceReceipt";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { useClubSettings } from "../../../../providers";
import { Button, CloseIcon, SlideSideBar, Spinner } from "../../../atoms";
import InvoiceInfoSection from "./InvoiceInfoSection";
import InvoiceItemsTable from "./InvoiceItemsTable";
import InvoiceTotalsTable from "./InvoiceTotalTable";
import { PaymentsActionsMenuPopover } from "./PaymentsActionsMenuPopover";
import {
  OpenCreateSidebarOptions,
  PaymentsSidebarProviderContext,
} from "./PaymentsSidebarProviderContext";

const PaymentsSidebarProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [sidebarState, setSidebarState] = useState<{
    invoiceNumber: string;
    memberId: string;
  }>();

  const isVisible = !!sidebarState;
  const invoiceNumber = sidebarState?.invoiceNumber;
  const memberId = sidebarState?.memberId;
  const { t } = useTranslation();
  const { api } = useGymflowModels();

  const { webSettings } = useClubSettings();
  const {
    data: invoice,
    isLoading,
    isError,
  } = useStaffGetInvoiceReceipt(
    { api, invoiceNumber },
    {
      enabled: isVisible,
    },
  );

  const { data: member } = useQuery(
    apiV2.staff.member.queries.idDeprecated(memberId),
  );
  const openUserStaffPaymentsSidebar = (options: OpenCreateSidebarOptions) => {
    setSidebarState(options);
  };

  const closeSidebar = useCallback(() => {
    setSidebarState(undefined);
  }, []);

  return (
    <PaymentsSidebarProviderContext.Provider
      value={{ openUserStaffPaymentsSidebar }}
    >
      <SlideSideBar
        isOpen={isVisible}
        hide={closeSidebar}
        className="w-[37.5rem]"
      >
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Spinner className="size-12" />
          </div>
        )}
        {isError && (
          <div className="text-error-500 absolute inset-0 flex items-center justify-center font-medium">
            {t("components.paymentsSidebarProvider.notFound")}
          </div>
        )}
        {invoice ? (
          <div className="mx-auto w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-6">
            <div className="flex items-center justify-between">
              <div className="flex h-10 w-32 justify-start">
                <img
                  alt="Logo"
                  src={webSettings.logo}
                  className="object-contain"
                />
              </div>
              <div className="flex items-center justify-between gap-3">
                <PaymentsActionsMenuPopover
                  invoiceNumber={invoiceNumber}
                  member={member}
                  memberId={memberId}
                  closeSidebar={closeSidebar}
                />
                <Button intent="transparent" onClick={closeSidebar}>
                  <CloseIcon />
                </Button>
              </div>
            </div>
            {invoiceNumber && (
              <>
                <InvoiceInfoSection
                  memberId={memberId}
                  invoiceNumber={invoiceNumber}
                />
                <div>
                  <InvoiceItemsTable invoiceNumber={invoiceNumber} />
                  <InvoiceTotalsTable invoiceNumber={invoiceNumber} />
                </div>
              </>
            )}
          </div>
        ) : null}
      </SlideSideBar>
      {children}
    </PaymentsSidebarProviderContext.Provider>
  );
};

export default PaymentsSidebarProvider;
